import BpkText, {
  TEXT_STYLES,
} from '@skyscanner/backpack-web/bpk-component-text';
import { cssModules } from '@skyscanner/backpack-web/bpk-react-utils';

import HelpfulButton from '../HelpfulButton';
import ReviewImageGallery from '../ReviewImageGallery';
import ReviewParagraphs from '../ReviewParagraphs';
import ReviewRating from '../ReviewRating';
import ReviewReplies from '../ReviewReplies';

import type { ImagesItem } from '@hotels-hotel-detail/ui/types';
import type { MiniEventTracker } from '@shared/types/frontend/mini-event-tracker';
import type { Review } from '@shared/types/frontend/ubff/hotel-reviews';
import type { I18nService } from '@shared/types/i18n';

import STYLES from './ReviewCard.module.scss';

import { useHotelReviewsContext } from '@hotels-hotel-detail/data-access/hooks/useHotelReviewsContext';
import useTranslation from '@hotels-hotel-detail/data-access/stores/useTranslation';
import CountryFlag from '@hotels-hotel-detail/ui/CountryFlag';
import { buildAdditionalInfoTranslationButtonClicked } from '@hotels-hotel-detail/utils/mini-events/hotels-action';
import { getSourceLocale } from '@hotels-hotel-detail/utils/review-service';
import BpkBreakpoint, { BREAKPOINTS } from '@shared/ui/common/BpkBreakpoint';
import TranslationButton from '@shared/ui/components/Translation/TranslationButton';
import TranslationContent from '@shared/ui/components/Translation/TranslationContent';
import { withI18n } from '@shared/ui/contexts/i18n';
import { withMiniEventTracker } from '@shared/ui/contexts/mini-events';
import {
  ACTION_TYPE,
  CLICK_AREA,
} from '@shared/utils-frontend/mini-events/constants';

const cls = cssModules(STYLES);

const MAX_REPLIES_LIMIT_DESKTOP = 2;
const MAX_REPLIES_LIMIT_MOBILE = 3;
const MAX_REVIEW_CONTENT_LIMIT_DESKTOP = 3;
const MAX_REVIEW_CONTENT_LIMIT_MOBILE = 5;

type Props = {
  review: Review;
  reviewIndex: number;
  i18n: I18nService;
  miniEventTracker: MiniEventTracker;
};

const ReviewCard = ({ i18n, miniEventTracker, review, reviewIndex }: Props) => {
  const { hotelId } = useHotelReviewsContext();

  const {
    checkinDate,
    country,
    guestType,
    images,
    locale,
    logo,
    partner,
    partnerReviewId,
    rating,
    replies,
    review: reviewContent,
    reviewDate,
    title,
    translated,
    translationType,
  } = review;

  const {
    currentData,
    isPending,
    state: translationState,
    translate,
  } = useTranslation({
    contentType: 'hotel-review',
    sourceLocale: getSourceLocale({
      userLocale: i18n.culture.locale,
      reviewLocale: locale,
      replies,
    }),
    targetLocale: i18n.culture.locale,
    data: {
      review: reviewContent,
      replies,
    },
    excludedFields: [],
  });

  const handleTranslate = () => {
    translate();
    miniEventTracker.trackHotelsAction(
      ACTION_TYPE.TRANSLATION_BUTTON_CLICKED,
      buildAdditionalInfoTranslationButtonClicked({
        clickArea: CLICK_AREA.DETAIL_REVIEW_TRANSLATION_BUTTON,
        targetLocale: i18n.culture.locale,
        sourceLocale: locale,
      }),
    );
  };

  return (
    <BpkBreakpoint query={BREAKPOINTS.MOBILE}>
      {(isMobile: boolean) => (
        <div className={cls('ReviewCard')}>
          <div className={cls('ReviewCard__guestDescription')}>
            <div className={cls('ReviewCard__guestRating')}>
              <BpkText
                textStyle={isMobile ? TEXT_STYLES.label2 : TEXT_STYLES.heading5}
              >
                {guestType}
              </BpkText>
              {isMobile && (
                <ReviewRating logo={logo} rating={rating} partner={partner} />
              )}
            </div>
            {country && <CountryFlag text={country.code} url={country.flag} />}
            <div className={cls('ReviewCard__dateContainer')}>
              {reviewDate && (
                <BpkText textStyle={TEXT_STYLES.caption} tagName="p">
                  {reviewDate}
                </BpkText>
              )}
              {checkinDate && (
                <BpkText textStyle={TEXT_STYLES.caption} tagName="p">
                  {checkinDate}
                </BpkText>
              )}
            </div>
          </div>

          <div className={cls('ReviewCard__mainContent')}>
            {!isMobile && (
              <ReviewRating logo={logo} rating={rating} partner={partner} />
            )}
            <div className={cls('ReviewCard__reviewTitle')}>
              {title && !isPending && (
                <BpkText
                  textStyle={
                    isMobile ? TEXT_STYLES.heading5 : TEXT_STYLES.heading4
                  }
                  tagName="p"
                >
                  {title}
                </BpkText>
              )}
              <TranslationContent isPending={isPending} withTitle={!!title}>
                <div className={cls('ReviewCard__reviewContent')}>
                  <ReviewParagraphs
                    review={currentData.review}
                    limit={
                      isMobile
                        ? MAX_REVIEW_CONTENT_LIMIT_MOBILE
                        : MAX_REVIEW_CONTENT_LIMIT_DESKTOP
                    }
                  />
                </div>
              </TranslationContent>
            </div>
            {!!images.length && (
              <ReviewImageGallery
                images={images.map(({ url }) => ({ src: url })) as ImagesItem[]}
                reviewIndex={reviewIndex}
              />
            )}
            {currentData.replies && (
              <ReviewReplies
                replies={currentData.replies}
                limit={
                  isMobile
                    ? MAX_REPLIES_LIMIT_MOBILE
                    : MAX_REPLIES_LIMIT_DESKTOP
                }
                isPending={isPending}
              />
            )}
            <div className={cls('ReviewCard__buttonsContainer')}>
              {!!partnerReviewId && (
                <HelpfulButton
                  hotelId={hotelId}
                  partnerReviewId={partnerReviewId}
                  locale={locale}
                  partner={partner.id}
                />
              )}
              <TranslationButton
                translationState={translationState}
                onTranslate={handleTranslate}
                translationType={translationType}
                translated={translated}
              />
            </div>
          </div>
        </div>
      )}
    </BpkBreakpoint>
  );
};

export default withMiniEventTracker(withI18n(ReviewCard));
